import Vue from 'vue'
import VueRouter from 'vue-router'
import { AuthenticationService, ProfileService } from '@services'
import CashCallService from '@services/CashCallService'
import { useInvoiceAlerts } from '@composables'
import routes from '@routes'
import store from '@store'

Vue.use(VueRouter)

const router = new VueRouter({
  routes,
  mode: 'history',
  linkActiveClass: 'active',
  scrollBehavior: (to, __, savedPosition) => {
    if (savedPosition) return savedPosition

    if (to.hash)
      return {
        selector: to.hash
      }

    return {
      x: 0,
      y: 0
    }
  }
})

// LIQ-2609: a cash call download link sends a logged-out user who is past the
// public-link limits to login with ?next=<that link>. Remember the link through
// the login steps (localStorage, as a magic-link login opens a new tab) and open
// it once logged in, so the download continues. Only an exact cash call
// download path is ever followed, so ?next= can't send anyone elsewhere.
const CASH_CALL_DOWNLOAD_PATH = /^\/backend\/api\/(?:v1\/)?cashcall\/download\/([0-9a-f-]{36})\/?$/i
const PENDING_DOWNLOAD_KEY = 'cashCallDownloadAfterLogin'
const PENDING_DOWNLOAD_MAX_AGE_MS = 30 * 60 * 1000
// The server sends the user back here again if it doesn't see them as logged
// in; skip a link opened in the last minute so that can't become a loop.
const LAST_DOWNLOAD_KEY = 'cashCallDownloadOpened'
const LAST_DOWNLOAD_WINDOW_MS = 60 * 1000

const readStored = (key) => {
  try {
    return JSON.parse(window.localStorage.getItem(key) || 'null')
  } catch (e) {
    return null
  }
}

const rememberCashCallDownload = (path) => {
  if (typeof path !== 'string' || !CASH_CALL_DOWNLOAD_PATH.test(path)) return
  const last = readStored(LAST_DOWNLOAD_KEY)
  if (last && last.path === path && Date.now() - last.at < LAST_DOWNLOAD_WINDOW_MS) return
  try {
    window.localStorage.setItem(PENDING_DOWNLOAD_KEY, JSON.stringify({ path, at: Date.now() }))
  } catch (e) {}
}

const openPendingCashCallDownload = async () => {
  const pending = readStored(PENDING_DOWNLOAD_KEY)
  if (!pending) return
  try {
    window.localStorage.removeItem(PENDING_DOWNLOAD_KEY)
  } catch (e) {}
  const match = CASH_CALL_DOWNLOAD_PATH.exec(pending.path || '')
  if (!match) return
  if (Date.now() - pending.at > PENDING_DOWNLOAD_MAX_AGE_MS) return
  try {
    window.localStorage.setItem(LAST_DOWNLOAD_KEY, JSON.stringify({ path: pending.path, at: Date.now() }))
  } catch (e) {}
  // The SPA login is JWT-only (no Django session), so ask the API for the
  // file URL with the token in the Authorization header rather than
  // navigating to the public link, which would bounce back to login.
  try {
    const res = await CashCallService.getDownloadUrl(match[1])
    const url = res && res.data && (res.data.url || (res.data.data && res.data.data.url))
    if (url) window.location.assign(url)
  } catch (e) {
    Vue.toasted.show('Unable to open the cash call download.', { type: 'error' })
  }
}

router.beforeEach(async (to, from, next) => {
  rememberCashCallDownload(to.query.next)

  // The vue-router-user-roles library (see main.js) enforces each route's
  // meta.permissions and silently redirects a denied user — e.g. a non-
  // superuser hitting /invoicing is pushed to the Dashboard. It shows no
  // message, so stash a flag here (beforeEach runs before the library's
  // redirect) that the Dashboard reads on mount to show an access-denied
  // toast. Scoped to Invoicing to avoid firing on unrelated redirects.
  if (to.name === 'Invoicing') {
    const user = store.getters.user
    if (!user || !user.is_superuser) {
      try { window.sessionStorage.setItem('accessDeniedPage', 'invoicing') } catch (e) {}
    }
  }

  // Handle special redirect query params (BDX only)
  if (to.query['manage-bdx']) {
    const role = store.state.auth.user.role
    return role === 'Guest'
      ? next()
      : next({ name: 'ManageBDX' })
  }

  // Cash Call route protection
  if (to.meta.requiresCashCallAccess) {
    const user = store.getters.user

    if (!user || user.role === 'Guest') {
      return next({ name: 'Home' })
    }

    if (!user.can_access_cash_call) {
      return next({ name: 'Dashboard' })
    }
  }

  // Page CSS class
  document.body.className = `${(to.name || '').toLowerCase()}-page`

  next()
})


router.beforeResolve(async (to, from,  next) => {
  const isAuthenticated = store.getters.authenticated
  const user = store.getters.user

  if (isAuthenticated) {
    await ProfileService.getInfo()

    // Superuser-only invoice attention check — same "hit on every page"
    // cadence as the profile fetch above, but fired without awaiting so a
    // slow/unreachable Lincoln never delays the route transition itself.
    // The header icon / page banner just update reactively once it resolves.
    if (user.is_superuser) {
      useInvoiceAlerts().refresh()
    }

    if (!user.member_is_active) {
      Vue.toasted.show('User account is deactivated!', {
        type: 'error'
      })

      await AuthenticationService.logout()
    } else {
      // Logged in: continue a cash call download that sent them to login.
      openPendingCashCallDownload()
    }

    //Cash Call enforcement
    if (to.meta.requiresCashCallAccess) {
      if (!user.can_access_cash_call) {
        Vue.toasted.show('You do not have access to Cash Call.', {
          type: 'error'
        })

        return next({ name: 'Dashboard' }) // or Home / Forbidden
      }
    }

    // Superuser-only route enforcement (e.g. /invoicing). The page and its
    // APIs are superuser-only; without this a non-superuser hitting the URL
    // directly loads a broken page (403s behind the scenes) instead of a
    // clean access-denied. Redirect to the Dashboard. Firing a toast from
    // inside the guard here is unreliable (the source page is torn down
    // mid-navigation before it renders), so we pass a flag through the
    // redirect and the Dashboard shows the message once it has mounted.
    next()
  } else {
    next()
  }
})

export default router
